import "./Home.css";
import SiteHeader from "./SiteHeader";
import BurgerMenu from "./BurgerMenu";
import { PLANS, formatPrice } from "./plans";

// Pop-up de souscription pour les comptes en attente : le gérant vient de
// créer son compte via l'invitation, il choisit ici son offre. Volontairement
// non fermable — l'espace n'existe pas tant que l'abonnement n'est pas
// souscrit, et l'API refuse de toute façon tout le reste à un compte pending.
function PendingOffersModal({ onSelectPlan, onLogout }) {
  return (
    <div className="home-offers-overlay">
      <div className="home-offers-modal">
        <div className="home-offers-head">
          <h2>Bienvenue ! Choisissez votre formule</h2>
        </div>
        <p className="home-offers-sub">
          Votre espace sera activé dès la souscription de votre abonnement.
          Sans souscription dans les 30 minutes suivant la création du compte,
          l'inscription est annulée.
        </p>
        <div className="home-offers-list">
          {PLANS.map((plan) => (
            <div
              key={plan.id}
              className={`home-offer${plan.featured ? " home-offer-featured" : ""}`}
            >
              <div className="home-offer-info">
                <span className="home-offer-name">
                  {plan.name}
                  {plan.featured && (
                    <span className="rc-badge home-offer-flag">{plan.featuredBadge}</span>
                  )}
                </span>
                <span className="home-offer-desc">{plan.tier}</span>
              </div>
              <div className="home-offer-actions">
                <button
                  className="rc-btn rc-btn-outline home-offer-btn"
                  onClick={() => onSelectPlan({ planId: plan.id, billing: "monthly" })}
                >
                  {formatPrice(plan.monthly)} € / mois
                </button>
                <button
                  className="rc-btn rc-btn-primary home-offer-btn"
                  onClick={() => onSelectPlan({ planId: plan.id, billing: "annual" })}
                >
                  {formatPrice(plan.annual)} € / an · {plan.annualBadge}
                </button>
              </div>
            </div>
          ))}
        </div>
        <button className="home-offers-later" onClick={onLogout}>
          Se déconnecter
        </button>
      </div>
    </div>
  );
}

function Home({ user, onLogout, onNavigate, onSelectPlan }) {
  // Un commercial n'a que ses outils : pas de fonctions commerçants
  const isSale = user.role === "Sale";
  // Compte en attente : la pop-up de souscription couvre tout l'écran et ne se
  // ferme pas — l'API refuse de toute façon tout à un compte pending.
  const mustSubscribe = user.status === "pending" && !isSale;
  // Fonctionnalités des plans payants : personnalisation et statistiques
  const hasPremiumPlan =
    !isSale &&
    !!user.organization_id &&
    (user.role === "Admin" || ["Premium", "Extra"].includes(user.plan));
  // Scénarios d'e-mails automatiques : plan Extra uniquement
  const hasExtraPlan =
    !isSale &&
    !!user.organization_id &&
    (user.role === "Admin" || user.plan === "Extra");
  return (
    <div className="home-container">
      <SiteHeader onLogoClick={() => onNavigate("landing")}>
        {user.role === "Admin" && (
          <button className="rc-btn rc-btn-primary site-header-btn" onClick={() => onNavigate("admin")}>
            Admin
          </button>
        )}
        {/* Mon compte, Facturations, Abonnement et Déconnexion vivent ici */}
        <BurgerMenu user={user} onNavigate={onNavigate} onLogout={onLogout} />
      </SiteHeader>
      <main className="home-main">
        <h2>Bienvenue, {user.surname} {user.name}</h2>
        <p className="home-subtitle">
          {isSale ? "Vos outils de commercial" : "Gérez vos cartes de fidélité"}
        </p>
        <div className="home-actions">
          {!isSale && (
          <button className="home-action-btn" onClick={() => onNavigate("generate-qr")}>
            <span className="home-action-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="1" />
                <rect x="14" y="3" width="7" height="7" rx="1" />
                <rect x="3" y="14" width="7" height="7" rx="1" />
                <rect x="14" y="14" width="3" height="3" />
                <path d="M21 14h-3v3" />
                <path d="M18 21h3v-3" />
              </svg>
            </span>
            <span className="home-action-text">
              <span className="home-action-label">Générer QR Code</span>
              <span className="home-action-desc">Créer un QR pour vos clients</span>
            </span>
          </button>
          )}
          {!isSale && (
          <button className="home-action-btn" onClick={() => onNavigate("scan")}>
            <span className="home-action-icon">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                <path d="M4 7V4h3" />
                <path d="M20 7V4h-3" />
                <path d="M4 17v3h3" />
                <path d="M20 17v3h-3" />
                <line x1="5" y1="12" x2="19" y2="12" />
              </svg>
            </span>
            <span className="home-action-text">
              <span className="home-action-label">Scanner une carte</span>
              <span className="home-action-desc">Ajouter / retirer des points</span>
            </span>
          </button>
          )}
          {user.role === "User" && !!user.organization_id && (
            <button className="home-action-btn" onClick={() => onNavigate("staff")}>
              <span className="home-action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="9" cy="8" r="3.5" />
                  <path d="M2.5 20c0-3.6 2.9-6 6.5-6s6.5 2.4 6.5 6" />
                  <circle cx="17" cy="9" r="2.5" />
                  <path d="M17.5 14.5c2.4.4 4 2.3 4 4.9" />
                </svg>
              </span>
              <span className="home-action-text">
                <span className="home-action-label">Sous-comptes</span>
                <span className="home-action-desc">Comptes employés : scan et QR</span>
              </span>
            </button>
          )}
          {hasPremiumPlan && (
            <button className="home-action-btn" onClick={() => onNavigate("customize")}>
              <span className="home-action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <circle cx="12" cy="12" r="3" />
                  <path d="M12 1v4M12 19v4M4.22 4.22l2.83 2.83M16.95 16.95l2.83 2.83M1 12h4M19 12h4M4.22 19.78l2.83-2.83M16.95 7.05l2.83-2.83" />
                </svg>
              </span>
              <span className="home-action-text">
                <span className="home-action-label">Personnaliser</span>
                <span className="home-action-desc">Logo et couleurs de vos cartes</span>
              </span>
            </button>
          )}
          {(user.role === "Admin" || isSale) && (
            <button className="home-action-btn" onClick={() => onNavigate("qr-links")}>
              <span className="home-action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="3" width="7" height="7" rx="1" />
                  <rect x="14" y="14" width="7" height="7" rx="1" />
                  <path d="M14 3h7v7" />
                  <path d="M21 3l-7 7" />
                  <rect x="3" y="14" width="7" height="7" rx="1" />
                </svg>
              </span>
              <span className="home-action-text">
                <span className="home-action-label">QR Dynamiques</span>
                <span className="home-action-desc">QR revendeurs configurables</span>
              </span>
            </button>
          )}
          {hasPremiumPlan && (
            <button className="home-action-btn" onClick={() => onNavigate("stats")}>
              <span className="home-action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="4" y1="20" x2="20" y2="20" />
                  <rect x="6" y="12" width="3" height="8" rx="1" />
                  <rect x="11" y="7" width="3" height="13" rx="1" />
                  <rect x="16" y="10" width="3" height="10" rx="1" />
                </svg>
              </span>
              <span className="home-action-text">
                <span className="home-action-label">Statistiques</span>
                <span className="home-action-desc">Suivi de vos clients</span>
              </span>
            </button>
          )}
          {hasExtraPlan && (
            <button className="home-action-btn" onClick={() => onNavigate("mail-scenarios")}>
              <span className="home-action-icon">
                <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="3" y="5" width="18" height="14" rx="2" />
                  <path d="M3 7l9 6 9-6" />
                </svg>
              </span>
              <span className="home-action-text">
                <span className="home-action-label">Scénarios d'e-mails</span>
                <span className="home-action-desc">Offres automatiques à vos clients</span>
              </span>
            </button>
          )}
        </div>
      </main>
      {mustSubscribe && onSelectPlan && (
        <PendingOffersModal onSelectPlan={onSelectPlan} onLogout={onLogout} />
      )}
    </div>
  );
}

export default Home;
